<template>
  <Teleport to="body">
    <div v-if="modelValue" ref="backdropRef" class="modal-backdrop" :class="{ 'modal-backdrop-draggable': draggable }" :style="{ zIndex: zIndexEfetivo }">
      <div ref="modalRef" class="modal-container shadow" role="dialog" aria-modal="true" :class="tamanhoClass" :style="modalStyle">

        <header
          class="modal-header"
          :class="{ 'cursor-grab': draggable, 'cursor-grabbing': draggable && dragging }"
          @mousedown.self="iniciarArrasto"
        >
          <h5 class="modal-title">{{ titulo }}</h5>
          <button type="button" class="btn-close btn-close-white" @click="tentarFechar" aria-label="Fechar"></button>
        </header>

        <div class="modal-body">
          <slot></slot>
        </div>

        <footer v-if="!ocultarConfirmar" class="modal-footer justify-content-end gap-2">
          <slot name="footer">
            <button type="button" class="btn btn-secondary" @click="tentarFechar" :disabled="loading">Cancelar</button>
            <button type="button" class="btn btn-primary" @click="confirmar" :disabled="loading">
              <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
              {{ textoConfirmar }}
            </button>
          </slot>
        </footer>
      </div>
    </div>
  </Teleport>

  <!-- Modal de Confirmação para sair com alterações não salvas -->
  <Teleport to="body">
    <ConfirmationModal
      ref="confirmacaoSairRef"
      title="Alterações não salvas"
      :message="'Existem alterações não salvas neste formulário.<br><strong>Deseja realmente sair?</strong>'"
      confirmButtonText="Sair sem salvar"
      cancelButtonText="Continuar editando"
      @confirmed="onConfirmacaoSair"
    />
  </Teleport>
</template>

<script setup>
import { computed, ref, watch, onMounted, onUnmounted, nextTick } from 'vue'
import ConfirmationModal from './ConfirmationModal.vue'
import { reservarZIndex, liberarZIndex, BASE_ZINDEX } from '@/composables/useZIndex'

/**
 * ModalFormulario: Componente base para formulários em janelas sobrepostas.
 * Reutiliza a identidade visual do ModalDeBusca para manter consistência.
 */
const props = defineProps({
  modelValue: Boolean,
  titulo: String,
  loading: Boolean,
  tamanho: { type: String, default: 'lg' }, // xs, sm, lg, xl, xxl
  ocultarConfirmar: { type: Boolean, default: false },
  hasChanges: { type: Boolean, default: false },
  draggable: { type: Boolean, default: true },
  textoConfirmar: { type: String, default: 'Salvar' },
  // Override opcional do z-index (piso). Quando null, usa o registry central.
  zIndex: { type: Number, default: null }
})

const emit = defineEmits(['update:modelValue', 'confirmar'])

const backdropRef = ref(null)
const modalRef = ref(null)
const confirmacaoSairRef = ref(null)
const fechamentoPendente = ref(false)

// --- Empilhamento (z-index) via registry central ---
// Reserva o próximo z-index ao abrir e libera ao fechar, garantindo que este
// modal fique sempre à frente de quem já estava aberto. A prop `zIndex`, quando
// informada, atua como piso (override opcional).
const tokenZIndex = ref(null)
const zIndexReservado = ref(null)
const zIndexEfetivo = computed(() => zIndexReservado.value ?? props.zIndex ?? BASE_ZINDEX)

// --- Estado de arrasto ---
const posX = ref(0)
const posY = ref(0)
const dragging = ref(false)
const dragOffset = ref({ x: 0, y: 0 })

const modalStyle = computed(() => {
  if (!props.draggable) return {}
  return {
    position: 'fixed',
    left: `${posX.value}px`,
    top: `${posY.value}px`,
    transform: 'none',
    margin: '0'
  }
})

const centralizarModal = () => {
  if (!props.draggable || !modalRef.value) return
  const rect = modalRef.value.getBoundingClientRect()
  posX.value = (window.innerWidth - rect.width) / 2
  posY.value = Math.max((window.innerHeight - rect.height) / 2, 20)
}

const iniciarArrasto = (e) => {
  if (!props.draggable || !modalRef.value) return
  e.preventDefault()
  dragging.value = true
  const rect = modalRef.value.getBoundingClientRect()
  dragOffset.value = { x: e.clientX - rect.left, y: e.clientY - rect.top }
  document.body.style.userSelect = 'none'
  document.body.style.pointerEvents = 'none'
  document.addEventListener('mousemove', mover)
  document.addEventListener('mouseup', pararArrasto)
}

const mover = (e) => {
  if (!dragging.value) return
  posX.value = e.clientX - dragOffset.value.x
  posY.value = e.clientY - dragOffset.value.y
}

const pararArrasto = () => {
  dragging.value = false
  document.body.style.userSelect = ''
  document.body.style.pointerEvents = ''
  document.removeEventListener('mousemove', mover)
  document.removeEventListener('mouseup', pararArrasto)
}

const limparListenersArrasto = () => {
  document.removeEventListener('mousemove', mover)
  document.removeEventListener('mouseup', pararArrasto)
  document.body.style.userSelect = ''
  document.body.style.pointerEvents = ''
}

const tentarFechar = () => {
  if (props.hasChanges) {
    fechamentoPendente.value = true
    confirmacaoSairRef.value?.show()
  } else {
    fechar()
  }
}

const onConfirmacaoSair = (confirmou) => {
  if (confirmou) {
    fechar()
  }
  fechamentoPendente.value = false
}

const fechar = () => emit('update:modelValue', false)
const confirmar = () => emit('confirmar')

const tamanhoClass = computed(() => {
  if (props.tamanho === 'xs') return 'modal-xs'
  if (props.tamanho === 'sm') return 'modal-sm'
  if (props.tamanho === 'xl') return 'modal-xl'
  if (props.tamanho === 'xxl') return 'modal-xxl'
  return '' // default LG
})

// Listener global para teclas Escape e Enter
// Só processa o evento se o target estiver DENTRO deste modal (backdropRef),
// garantindo que num cenário de modais aninhados apenas o modal ativo (topo) responda.
const handleKeydown = (e) => {
  if (!props.modelValue) return
  if (!backdropRef.value?.contains(e.target)) return

  if (e.key === 'Escape') {
    e.preventDefault()
    tentarFechar()
  }
  if (e.key === 'Enter' && !props.ocultarConfirmar && !props.loading) {
    // Não dispara em textarea nem com Shift+Enter
    if (e.target.tagName !== 'TEXTAREA' && !e.shiftKey) {
      e.preventDefault()
      confirmar()
    }
  }
}

watch(() => props.modelValue, (novoValor) => {
  if (novoValor) {
    const { token, zIndex } = reservarZIndex(props.zIndex)
    tokenZIndex.value = token
    zIndexReservado.value = zIndex
    if (props.draggable) {
      nextTick(() => centralizarModal())
    }
  } else {
    liberarZIndex(tokenZIndex.value)
    tokenZIndex.value = null
    zIndexReservado.value = null
  }
}, { immediate: true })

onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
  limparListenersArrasto()
  // Segurança: libera o z-index caso o modal seja desmontado ainda aberto
  liberarZIndex(tokenZIndex.value)
  tokenZIndex.value = null
  zIndexReservado.value = null
})
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
}

.modal-backdrop-draggable {
  display: block;
}

/* GUARDIÃO: Variáveis dinâmicas para suportar Modo Escuro e Modo Claro */
.modal-container {
  width: 90%;
  max-width: 800px;
  background-color: var(--bs-body-bg);
  color: var(--bs-body-color);
  border: 1px solid var(--bs-border-color-translucent);
  border-radius: 0.5rem;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

.modal-xs { max-width: 340px; }
.modal-sm { max-width: 500px; }
.modal-xl { max-width: 1140px; }
.modal-xxl { max-width: 1400px; }

/* O Cabeçalho mantém a âncora visual da marca */
.modal-header {
  padding: 1rem 1.5rem;
  background-color: #34495E;
  color: #FFFFFF;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #1f2d3a;
  flex-shrink: 0;
  border-top-left-radius: 0.5rem;
  border-top-right-radius: 0.5rem;
}

.cursor-grab { cursor: grab; }
.cursor-grabbing { cursor: grabbing; user-select: none; }

.modal-title {
  font-size: 1.25rem;
  font-weight: 500;
  margin: 0;
  cursor: text;
  user-select: text;
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex-grow: 1;
}

.modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--bs-border-color-translucent);
  flex-shrink: 0;
  display: flex;
  background-color: var(--bs-tertiary-bg);
  border-bottom-left-radius: 0.5rem;
  border-bottom-right-radius: 0.5rem;
  transition: background-color 0.3s ease, border-color 0.3s ease;
}
</style>
